import { Paper, Switch, Tooltip, Typography } from '@mui/material'

import { Box } from '@/components/ui'

import { requirementDisplay } from '../../utils/requirementDisplay'

export interface ProductFlagRowProps {
  label: string
  checked: boolean
  /** False while a lower package is selected, which locks the row. */
  isEditable: boolean
  /** The product flag this one sits behind, when it sits behind one. */
  requirementLabel?: string
  /** False while that product flag is off, which is what keeps this switch off. */
  isRequirementMet?: boolean
  /** Why the last save did not apply this product flag. */
  errorMessage?: string
  onChange?: (checked: boolean) => void
}

/** One product flag and its switch. */
export const ProductFlagRow = ({
  label,
  checked,
  isEditable,
  requirementLabel,
  isRequirementMet = true,
  errorMessage,
  onChange,
}: ProductFlagRowProps) => {
  const { isBlocked, color: requirementColor } = requirementDisplay({
    isEditable,
    isRequirementMet,
    checked,
  })
  const canToggle = isEditable && !isBlocked

  const requirementHint =
    isBlocked && requirementLabel !== undefined
      ? `Turn on ${requirementLabel} first.`
      : ''

  return (
    <Paper variant='infoRow'>
      <Box variant='flexBetween'>
        <Box variant='flexColumn' gap='xs' fullWidth={false}>
          <Typography
            variant='subtitle2'
            // Washed out rather than merely quieter, so a locked row reads as
            // unavailable at a glance instead of just being lower contrast.
            color={isEditable ? 'text.primary' : 'text.disabled'}
          >
            {label}
          </Typography>
          {/* Stated on the row rather than only in the switch's tooltip: a hover
              hides the one thing the operator needs to know to act, and it would
              leave the requirement invisible on the rows that are already on. */}
          {requirementLabel !== undefined && (
            <Typography variant='caption' color={requirementColor}>
              Requires {requirementLabel}
            </Typography>
          )}
          {errorMessage !== undefined && (
            <Typography variant='caption' color='error'>
              {errorMessage}
            </Typography>
          )}
        </Box>
        {/* A disabled switch swallows pointer events, so the hint goes on a wrapper
            rather than on the control itself. */}
        <Tooltip title={requirementHint} placement='top-end'>
          <span>
            <Switch
              checked={checked}
              disabled={!canToggle}
              slotProps={{ input: { 'aria-label': label } }}
              onChange={(event) => onChange?.(event.target.checked)}
            />
          </span>
        </Tooltip>
      </Box>
    </Paper>
  )
}
